import React, { useState, useEffect } from 'react';
import { ClockIcon, CalendarIcon, MapPinIcon, UserGroupIcon, PhoneIcon } from '@heroicons/react/24/outline';
import axiosInstance from '../../utils/axios';
import { formatDate } from '../../utils/dateUtils';

function RideBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await axiosInstance.get('/api/bookings/driver');
        console.log('Fetched driver bookings:', response.data);
        setBookings(response.data);
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError(err.response?.data?.message || 'Failed to load bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const handleUpdateStatus = async (bookingId, newStatus) => {
    try {
      await axiosInstance.put(`/api/bookings/${bookingId}/status`, { status: newStatus });
      
      // Update the booking status locally
      setBookings(bookings.map(booking => 
        booking.id === bookingId ? {...booking, status: newStatus} : booking
      ));
    } catch (err) {
      console.error('Error updating booking status:', err);
      alert('Failed to update booking status. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <ClockIcon className="animate-spin h-8 w-8 text-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Ride Bookings</h1>
      
      {bookings.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No bookings have been made for your rides yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {bookings.map((booking) => (
            <div key={booking.id} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="h-5 w-5 text-indigo-500" />
                    <span className="text-sm font-medium">{formatDate(booking.date || booking.Ride?.date)}</span>
                  </div>
                  <div className="flex space-x-2">
                    {(booking.serviceSession || booking.Ride?.serviceSession) && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {booking.serviceSession || booking.Ride?.serviceSession} Service
                      </span>
                    )}
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    booking.status === 'confirmed' 
                      ? 'bg-green-100 text-green-800'
                      : booking.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800'
                        : booking.status === 'cancelled' || booking.status === 'declined'
                      ? 'bg-red-100 text-red-800'
                      : booking.status === 'completed'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {booking.status}
                  </span>
                  </div>
                </div>

                {/* Rider Information */}
                {booking.rider && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Rider Information</h3>
                    <p className="text-sm text-gray-600">
                      {booking.rider.firstName} {booking.rider.lastName}
                    </p>
                    {booking.rider.phone && (
                      <div className="flex items-center mt-1 text-sm text-gray-600">
                        <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                        {booking.rider.phone}
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Pickup</p>
                      <p className="text-sm">
                        {booking.pickupStreet && booking.pickupCity && booking.pickupPostcode 
                          ? (
                            <div>
                              <div>{booking.pickupStreet}</div>
                              <div className="text-gray-600">{booking.pickupCity} {booking.pickupPostcode}</div>
                            </div>
                          )
                          : (booking.pickupLocation || booking.Ride?.pickupLocation || 'N/A')
                        }
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-2">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Destination</p>
                      <p className="text-sm">{booking.destination || booking.Ride?.dropoffLocation || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Departure Time</p>
                      <p className="text-sm">{booking.serviceTime || booking.Ride?.time || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <UserGroupIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Passengers</p>
                      <p className="text-sm">{booking.seats || booking.passengers || 1}</p>
                    </div>
                  </div>

                  {booking.specialRequests && (
                    <div className="mt-2 text-sm">
                      <p className="text-xs text-gray-500 mb-1">Special Requests</p>
                      <p className="text-sm bg-gray-50 p-2 rounded">{booking.specialRequests}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {booking.status !== 'cancelled' && booking.status !== 'declined' && booking.status !== 'completed' && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex space-x-2">
                      {booking.status !== 'completed' && (
                        <button
                          onClick={() => handleUpdateStatus(booking.id, 'completed')}
                          className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                        >
                          Mark Completed
                        </button>
                      )}
                      
                      {booking.status === 'pending' && (
                        <>
                        <button
                          onClick={() => handleUpdateStatus(booking.id, 'confirmed')}
                          className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                        >
                            Accept
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(booking.id, 'declined')}
                            className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            Decline
                        </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RideBookings; 